import { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import api, { fmtDate, errDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DataTable } from "../components/DataTable";
import { PageHeader } from "../components/CrudPage";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search } from "lucide-react";
import { toast } from "sonner";

const MODULE_LABELS = {
  dashboard: "Dashboard", sales: "Satış", sales_history: "Satış Geçmişi",
  categories: "Ana Kategoriler", subcategories: "Alt Kategoriler",
  products: "Ürünler", stock_movements: "Stok Hareketleri", equipment: "Envanter",
  agenda: "Ajanda", calendar: "Takvim", appointments: "Randevular",
  customers: "Müşteriler", suppliers: "Tedarikçiler",
  income: "Gelir", expense: "Gider", cash: "Kasa",
  reports: "Raporlar", users: "Kullanıcılar",
  permissions: "Yetkilendirme", settings: "Ayarlar", auth: "Oturum",
};

const ACTION_LABELS = {
  create: "Ekleme", update: "Güncelleme", delete: "Silme", sale: "Satış",
  refund: "İade", stock: "Stok Hareketi", tahsilat: "Tahsilat",
  veresiye: "Veresiye", permissions: "Yetki Değişikliği", login: "Giriş",
};

export default function AuditLog() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ user_id: "", module: "", date_from: "", date_to: "" });
  const [loading, setLoading] = useState(true);

  const isSuperAdmin = user?.role === "super_admin";

  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get("/users", { params: { limit: 100 } }).then((r) => setUsers(r.data.items)).catch(() => {});
  }, [isSuperAdmin]);

  const load = useCallback(async () => {
    if (!isSuperAdmin) return;
    setLoading(true);
    try {
      const { data: d } = await api.get("/activity-logs", { params: { page, search, limit: 20, ...filters } });
      setData(d);
    } catch (e) { toast.error(errDetail(e)); }
    finally { setLoading(false); }
  }, [isSuperAdmin, page, search, filters]);

  useEffect(() => { load(); }, [load]);

  if (user && !isSuperAdmin) return <Navigate to="/" replace />;

  return (
    <div data-testid="audit-log-page">
      <PageHeader title="Denetim Kaydı" subtitle="Kullanıcıların sistemde yaptığı işlemlerin kaydı" />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <form onSubmit={(e) => { e.preventDefault(); setPage(1); setSearch(searchInput); }} className="flex items-center gap-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input data-testid="audit-search-input" className="w-56 rounded-none pl-9" placeholder="Kullanıcı, açıklama..."
              value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
          </div>
          <Button type="submit" variant="outline" className="rounded-none">Ara</Button>
        </form>
        <Select value={filters.user_id || "all"}
          onValueChange={(v) => { setPage(1); setFilters({ ...filters, user_id: v === "all" ? "" : v }); }}>
          <SelectTrigger data-testid="audit-user-filter" className="w-48 rounded-none"><SelectValue placeholder="Kullanıcı" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Kullanıcı: Tümü</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.module || "all"}
          onValueChange={(v) => { setPage(1); setFilters({ ...filters, module: v === "all" ? "" : v }); }}>
          <SelectTrigger data-testid="audit-module-filter" className="w-48 rounded-none"><SelectValue placeholder="Modül" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Modül: Tümü</SelectItem>
            {Object.entries(MODULE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid="audit-date-from" type="date" className="w-40 rounded-none" value={filters.date_from}
          onChange={(e) => { setPage(1); setFilters({ ...filters, date_from: e.target.value }); }} />
        <Input data-testid="audit-date-to" type="date" className="w-40 rounded-none" value={filters.date_to}
          onChange={(e) => { setPage(1); setFilters({ ...filters, date_to: e.target.value }); }} />
      </div>

      <DataTable testId="audit-log-table" columns={[
        { key: "created_at", label: "Tarih / Saat", render: (r) => fmtDate(r.created_at) },
        { key: "user_name", label: "Kullanıcı", render: (r) => <span className="font-medium">{r.user_name}</span> },
        { key: "module", label: "Modül", render: (r) => MODULE_LABELS[r.module] || r.module },
        { key: "action", label: "Aksiyon", render: (r) => ACTION_LABELS[r.action] || r.action },
        { key: "description", label: "Açıklama" },
      ]} data={data.items} loading={loading} page={page} pages={data.pages} total={data.total} onPage={setPage} />
    </div>
  );
}
